// vavci-pdp-variants.js — Lógica del selector de variantes por OPCIÓN de la PDP.
//
// El selector anterior pintaba un botón por variante. En una ficha como el tinte Fanola
// (118 variantes) eso era una lista plana de 118 botones con el título concatenado
// «Familia / Tono». Aquí las opciones se tratan por separado y se encadenan: la opción de
// posición N se filtra con lo elegido en las posiciones anteriores.
//
// La matriz es PARCIAL a propósito — no existe toda la combinación familia × tono, solo
// las reales — así que nada asume que un valor esté disponible en todas las ramas.
//
// Las funciones son puras y no tocan el DOM: se prueban desde scripts/__tests__/.

/**
 * Busca la variante que corresponde a una selección COMPLETA.
 * @param {{variants: Array, selection: string[]}} args
 * @returns {object|null} La variante, o null si esa combinación no existe.
 */
export function matchVariant({ variants, selection }) {
  return (
    (variants ?? []).find(
      (v) => v.options.length === selection.length && v.options.every((val, i) => val === selection[i]),
    ) ?? null
  );
}

/**
 * Estado de cada valor de una opción, encadenado con lo ya elegido.
 *
 * Solo restringe con las posiciones ANTERIORES: elegir Familia filtra los Tonos, pero
 * elegir un Tono no debe dejar una sola Familia elegible — el cliente tiene que poder
 * saltar de familia en cualquier momento.
 *
 * @param {{variants: Array, optionValues: string[][], selection: string[], position: number}} args
 * @returns {Array<{value: string, exists: boolean, available: boolean}>} En el orden de la opción.
 */
export function valueStates({ variants, optionValues, selection, position }) {
  const encaja = (v, value) =>
    v.options[position] === value && selection.slice(0, position).every((sel, i) => v.options[i] === sel);

  return (optionValues[position] ?? []).map((value) => {
    const candidatas = (variants ?? []).filter((v) => encaja(v, value));
    return {
      value,
      exists: candidatas.length > 0,
      available: candidatas.some((v) => v.available),
    };
  });
}

/**
 * Aplica la elección del cliente y arrastra las opciones siguientes a una combinación real.
 *
 * Cambiar de familia puede dejar huérfano el tono elegido, porque la matriz es parcial.
 * En ese caso se salta al primer tono de la familia nueva, prefiriendo uno con stock;
 * si toda la rama está agotada se coge el primero que exista, para no dejar al cliente
 * en una combinación inexistente.
 *
 * @param {{variants: Array, optionValues: string[][], selection: string[], position: number, value: string}} args
 * @returns {string[]} Selección completa y válida.
 */
export function resolveSelection({ variants, optionValues, selection, position, value }) {
  const next = selection.slice();
  next[position] = value;

  for (let p = position + 1; p < next.length; p += 1) {
    const estados = valueStates({ variants, optionValues, selection: next, position: p });
    if (estados.some((e) => e.value === next[p] && e.exists)) continue;
    const sustituto = estados.find((e) => e.exists && e.available) ?? estados.find((e) => e.exists);
    if (sustituto) next[p] = sustituto.value;
  }

  return next;
}

// Marcadores de money_format de Shopify → separador de miles y de decimales.
// El tema los repinta en JS y deben salir igual que los que pinta Liquid con
// el filtro | money; hardcodear un formato descuadra la ficha (la tienda usa
// «€16,50», con el símbolo delante).
const MONEY_PLACEHOLDERS = {
  amount: { miles: ',', decimal: '.', decimales: 2 },
  amount_no_decimals: { miles: ',', decimal: '.', decimales: 0 },
  amount_with_comma_separator: { miles: '.', decimal: ',', decimales: 2 },
  amount_no_decimals_with_comma_separator: { miles: '.', decimal: ',', decimales: 0 },
  amount_with_apostrophe_separator: { miles: "'", decimal: '.', decimales: 2 },
};

const MONEY_FORMAT_FALLBACK = '€{{amount_with_comma_separator}}';

/**
 * Céntimos → precio con el `shop.money_format` de la tienda (lo sirve Liquid
 * en un atributo data-*). Un formato vacío o sin marcador reconocible cae al
 * de esta tienda. Entrada no numérica → 0.
 * @param {number} cents
 * @param {string} [moneyFormat] p. ej. «€{{amount_with_comma_separator}}».
 * @returns {string}
 */
export function formatMoney(cents, moneyFormat) {
  const n = Number(cents);
  const safe = Number.isFinite(n) ? n : 0;

  const patron = /\{\{\s*(\w+)\s*\}\}/.exec(String(moneyFormat || ''));
  const clave = patron && MONEY_PLACEHOLDERS[patron[1]] ? patron[1] : null;
  const formato = clave ? String(moneyFormat) : MONEY_FORMAT_FALLBACK;
  const marcador = clave ? patron[0] : '{{amount_with_comma_separator}}';
  const { miles, decimal, decimales } = MONEY_PLACEHOLDERS[clave || 'amount_with_comma_separator'];

  const fijo = (safe / 100).toFixed(decimales);
  const [entero, resto] = fijo.split('.');
  const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
  const numero = resto ? conMiles + decimal + resto : conMiles;

  return formato.replace(marcador, numero);
}

/**
 * Céntimos → precio con el formato que Liquid dejó en `[data-vc-money]` (el
 * `shop.money_format` de la tienda). Sin ese dato usa el formato por defecto.
 * @param {number} cents
 * @returns {string}
 */
export function formatEuros(cents) {
  const fuente = typeof document !== 'undefined' && document.querySelector('[data-vc-money]');
  return formatMoney(cents, fuente ? fuente.getAttribute('data-vc-money') : '');
}

/**
 * Total del CTA «Añadir al carrito · total» = precio × cantidad.
 * La cantidad se fija en mínimo 1 (el stepper no baja de ahí) y se trunca si
 * llega decimal; un precio inválido o negativo cuenta como 0.
 * @param {{cents: number, qty: number}} args
 * @returns {number} Total en céntimos.
 */
export function totalCents({ cents, qty }) {
  const c = Number(cents);
  const q = Math.floor(Number(qty));
  const safeCents = Number.isFinite(c) && c > 0 ? c : 0;
  const safeQty = Number.isFinite(q) && q >= 1 ? q : 1;
  return safeCents * safeQty;
}

/**
 * Precio EFECTIVO del selector Compra única / Suscríbete (WU4).
 *
 * La variante llega del catálogo JSON del bloque purchase_options con sus
 * asignaciones REALES (selling_plan_allocations preformateadas por Liquid):
 * allocs: [{ plan, cents, price, pct }]. Reglas:
 *   - modo distinto de 'sub' (o sin variante) → precio de la variante, sin plan.
 *   - modo 'sub' sin asignaciones → compra única (nunca se inventa un descuento).
 *   - modo 'sub' con plan desconocido → PRIMER plan del grupo (el default del select).
 *
 * @param {{variant: object|null, mode: string, planId: string|number}} args
 * @returns {{planId: string|null, cents: number, price: string, pct: number}}
 */
export function effectivePurchase({ variant, mode, planId }) {
  const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
  const once = { planId: null, cents: num(variant?.cents), price: variant?.price ?? '', pct: 0 };
  if (!variant || mode !== 'sub') return once;
  const allocs = Array.isArray(variant.allocs) ? variant.allocs : [];
  if (!allocs.length) return once;
  const alloc = allocs.find((a) => String(a.plan) === String(planId)) ?? allocs[0];
  return { planId: String(alloc.plan), cents: num(alloc.cents), price: alloc.price ?? '', pct: num(alloc.pct) };
}

/* ── Enganche con el DOM ──────────────────────────────────────────────────────────────
   Todo lo de abajo es pegamento: lee el bloque JSON que imprime la sección, pinta el
   estado de cada botón y avisa al resto de módulos. La lógica de arriba no sabe que
   existe un navegador, y por eso se puede probar. */

/*
 * Total vivo del CTA y de la barra fija. Cada elemento [data-vc-total] lleva su
 * precio en céntimos en data-vc-cents (Liquid pinta el inicial; aplicar() lo
 * actualiza al cambiar de variante) y aquí solo se multiplica por la cantidad.
 * Va SEPARADO de initPicker a propósito: en un producto de variante única no hay
 * selector, pero el total sigue teniendo que reaccionar al stepper.
 * El orden de los listeners es fiable: el script inline de la sección (que
 * actualiza el valor del input) se registra antes que este módulo diferido.
 */
function initTotal(pdp) {
  const qtyEl = pdp.querySelector('[data-vc-qty]');
  const totales = Array.from(pdp.querySelectorAll('[data-vc-total]'));
  if (!totales.length) return () => {};

  const repaint = () => {
    const qty = qtyEl ? qtyEl.value : 1;
    totales.forEach((el) => {
      el.textContent = formatEuros(totalCents({ cents: Number(el.getAttribute('data-vc-cents')), qty }));
    });
  };

  pdp.querySelectorAll('[data-vc-qty-inc],[data-vc-qty-dec]').forEach((b) => b.addEventListener('click', repaint));
  if (qtyEl) qtyEl.addEventListener('input', repaint);
  return repaint;
}

function initPicker(pdp, repaintTotal) {
  const box = pdp.querySelector('[data-vc-options]');
  const datos = box && box.querySelector('script[data-vc-variants]');
  if (!datos) return;

  let catalogo;
  try {
    catalogo = JSON.parse(datos.textContent);
  } catch {
    return; // JSON roto: la ficha se queda con lo que pintó Liquid, que ya es usable.
  }
  const variants = catalogo.variants ?? [];
  const optionValues = catalogo.values ?? [];
  if (!variants.length || !optionValues.length) return;

  const inicial = variants.find((v) => String(v.id) === box.getAttribute('data-vc-current')) ?? variants[0];
  let selection = inicial.options.slice();

  const hiddenId = pdp.querySelector('[data-vc-variant-id]');
  const priceEl = pdp.querySelector('[data-vc-price]');
  const compareEl = pdp.querySelector('[data-vc-compare]');
  const saveEl = pdp.querySelector('[data-vc-save]');
  const unitEl = pdp.querySelector('[data-vc-unit]');
  const skuWrapEl = pdp.querySelector('[data-vc-sku-wrap]');
  const skuEl = pdp.querySelector('[data-vc-sku]');
  const stockEl = pdp.querySelector('[data-vc-instock]');
  const addExtraEl = pdp.querySelector('[data-vc-add-extra]');
  const stickyVariantEl = pdp.querySelector('[data-vc-sticky-variant]');
  const totalEls = Array.from(pdp.querySelectorAll('[data-vc-total]'));
  const addBtns = pdp.querySelectorAll('[data-vc-add],[data-vc-sticky-add]');
  const txtAdd = box.getAttribute('data-vc-add-label') ?? '';
  const txtSold = box.getAttribute('data-vc-sold-label') ?? '';
  const botones = Array.from(box.querySelectorAll('[data-vc-val]'));

  function pintar() {
    optionValues.forEach((_, position) => {
      const estados = new Map(
        valueStates({ variants, optionValues, selection, position }).map((e) => [e.value, e]),
      );
      botones
        .filter((b) => Number(b.getAttribute('data-vc-opt')) === position)
        .forEach((b) => {
          const st = estados.get(b.getAttribute('data-vc-val'));
          const elegido = b.getAttribute('data-vc-val') === selection[position];
          // Un valor que no existe en esta rama se oculta en vez de deshabilitarse: con 118
          // tonos, dejar visibles los 108 imposibles es justo el problema que veníamos a quitar.
          b.hidden = !st?.exists;
          b.classList.toggle('is-on', elegido);
          b.classList.toggle('is-out', Boolean(st?.exists && !st.available));
          b.setAttribute('aria-pressed', elegido ? 'true' : 'false');
        });
      const eco = box.querySelector(`[data-vc-opt-current="${position}"]`);
      if (eco) eco.textContent = selection[position] ?? '';
    });
  }

  function aplicar() {
    const variant = matchVariant({ variants, selection });
    if (!variant) return;
    if (hiddenId) hiddenId.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price;
    if (compareEl) {
      compareEl.textContent = variant.compare ?? '';
      compareEl.style.display = variant.onsale ? '' : 'none';
    }
    if (saveEl) {
      saveEl.textContent = variant.save ?? '';
      saveEl.style.display = variant.onsale ? '' : 'none';
    }
    // Precio por unidad de medida («1,65 € / 100 ml»): viene preformateado por
    // Liquid en el catálogo; sin medida en esta variante, la línea se oculta.
    if (unitEl) {
      unitEl.textContent = variant.unit ?? '';
      unitEl.hidden = !variant.unit;
    }
    // Meta bajo el título: referencia (SKU) y punto verde de stock de ESTA variante.
    if (skuEl) skuEl.textContent = variant.sku ?? '';
    if (skuWrapEl) skuWrapEl.hidden = !variant.sku;
    if (stockEl) stockEl.hidden = !variant.available;
    // Formato elegido en la barra fija («Título · 1000 ml»).
    if (stickyVariantEl) stickyVariantEl.textContent = (variant.options ?? []).join(' / ');
    // Totales vivos (CTA y barra fija): cada elemento lleva SU precio en céntimos
    // y initTotal() los multiplica por la cantidad. El «compare» de la barra fija
    // usa el tachado y solo se ve en oferta.
    totalEls.forEach((el) => {
      const esCompare = el.getAttribute('data-vc-total') === 'compare';
      el.setAttribute('data-vc-cents', String((esCompare ? variant.ccents : variant.cents) ?? 0));
      if (esCompare) el.hidden = !variant.onsale;
    });
    if (addExtraEl) addExtraEl.hidden = !variant.available;
    // Ahora un tono agotado SÍ se puede seleccionar (el cliente tiene que poder ver que
    // existe), así que el botón de compra pasa a depender de la variante, no del render.
    // El botón principal guarda el rótulo en un span propio (el «· total» vive al
    // lado); en la barra fija el botón sigue siendo texto plano.
    addBtns.forEach((b) => {
      b.disabled = !variant.available;
      const lbl = b.querySelector('[data-vc-add-label]') ?? b;
      lbl.textContent = variant.available ? txtAdd : txtSold;
    });
    if (typeof repaintTotal === 'function') repaintTotal();
    // El selector de compra/suscripción (WU4) tiene su propio estado: se le avisa
    // por evento (igual que a la galería) y él repinta el precio efectivo si el
    // modo activo es suscripción.
    pdp.dispatchEvent(new CustomEvent('vavci:variant', { detail: { id: variant.id } }));
    // La galería tiene su propio estado: se le avisa por evento, no se le manipula.
    if (variant.media) pdp.dispatchEvent(new CustomEvent('vavci:show-media', { detail: { id: variant.media } }));
  }

  box.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-vc-val]');
    if (!b || b.hidden) return;
    selection = resolveSelection({
      variants,
      optionValues,
      selection,
      position: Number(b.getAttribute('data-vc-opt')),
      value: b.getAttribute('data-vc-val'),
    });
    pintar();
    aplicar();
  });

  pintar(); // Sin aplicar(): en la carga manda lo que ya renderizó Liquid.
}

/*
 * WU4 · Compra única / Suscríbete y ahorra. Solo existe si el bloque
 * purchase_options pintó (el producto tiene selling plans reales). Mantiene:
 *   - el input oculto selling_plan del form: con plan elegido lleva su id y va
 *     en el POST AJAX y en el pago dinámico; en compra única queda `disabled`
 *     (fuera de ambos envíos).
 *   - los totales del CTA y de la barra fija ([data-vc-total="price"]): en modo
 *     suscripción pasan al precio por entrega de la asignación real.
 * Escucha 'vavci:variant' (lo emite aplicar() al cambiar de formato) para
 * repintar precios/chip con las asignaciones de la variante nueva.
 */
function initPurchase(pdp, repaintTotal) {
  const box = pdp.querySelector('[data-vc-purchase]');
  const datos = box && box.querySelector('script[data-vc-po-data]');
  if (!datos) return;

  let catalogo;
  try {
    catalogo = JSON.parse(datos.textContent);
  } catch {
    return; // JSON roto: se queda la compra única que ya pintó Liquid.
  }
  const variants = catalogo.variants ?? [];
  if (!variants.length) return;

  const hiddenId = pdp.querySelector('[data-vc-variant-id]');
  const planInput = box.querySelector('[data-vc-po-plan-input]');
  const planSel = box.querySelector('[data-vc-po-plan]');
  const subCard = box.querySelector('[data-vc-po-subcard]');
  const onceEl = box.querySelector('[data-vc-po-once]');
  const subEl = box.querySelector('[data-vc-po-sub]');
  const pctEl = box.querySelector('[data-vc-po-pct]');
  const radios = Array.from(box.querySelectorAll('[data-vc-mode]'));
  const totalEls = Array.from(pdp.querySelectorAll('[data-vc-total="price"]'));

  const mode = () => (radios.find((r) => r.checked)?.value === 'sub' ? 'sub' : 'once');

  function apply() {
    const id = hiddenId ? hiddenId.value : '';
    const variant = variants.find((v) => String(v.id) === String(id)) ?? variants[0];
    const allocs = Array.isArray(variant.allocs) ? variant.allocs : [];

    // Una variante sin asignaciones no puede suscribirse: la tarjeta se oculta
    // y, si estaba elegida, se vuelve a compra única (nunca un plan inventado).
    if (subCard) subCard.hidden = !allocs.length;
    if (!allocs.length && mode() === 'sub') {
      const once = radios.find((r) => r.value !== 'sub');
      if (once) once.checked = true;
    }

    const planElegido = planSel ? planSel.value : allocs[0]?.plan;
    const eff = effectivePurchase({ variant, mode: mode(), planId: planElegido });

    if (planInput) {
      planInput.value = eff.planId ?? '';
      planInput.disabled = eff.planId == null;
    }

    // La tarjeta de suscripción enseña SIEMPRE su precio/descuento reales
    // (también en modo compra única: es lo que permite comparar).
    if (onceEl) onceEl.textContent = variant.price ?? '';
    const alloc = allocs.find((a) => String(a.plan) === String(planElegido)) ?? allocs[0];
    if (subEl) subEl.textContent = alloc ? (alloc.price ?? '') : '';
    if (pctEl) {
      const pct = alloc ? Number(alloc.pct) || 0 : 0;
      pctEl.textContent = `-${pct}%`;
      pctEl.hidden = pct <= 0;
    }

    // Total del CTA y de la barra fija = precio efectivo del modo × cantidad.
    totalEls.forEach((el) => el.setAttribute('data-vc-cents', String(eff.cents)));
    if (typeof repaintTotal === 'function') repaintTotal();
  }

  radios.forEach((r) => r.addEventListener('change', apply));
  if (planSel) planSel.addEventListener('change', apply);
  pdp.addEventListener('vavci:variant', apply);
}

function init() {
  document.querySelectorAll('[data-vc-pdp]').forEach((pdp) => {
    const repaintTotal = initTotal(pdp);
    initPicker(pdp, repaintTotal);
    initPurchase(pdp, repaintTotal);
  });
}

if (typeof document !== 'undefined') {
  // El script inline de la sección (no es módulo) necesita el mismo formato de
  // moneda para la barra de envío: se lo publicamos aquí.
  window.vcMoney = formatEuros;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
